import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Button,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";

import { Formik, Form, ErrorMessage, Field } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Breadcrumb from "components/Common/Breadcrumb";
import { apiGet, apiPost, apiPut, apiDelete } from "helpers/apiService";
import SafeField from "helpers/SafeField";
import ActionEdit from "components/buttons/actionEdit";
import ActionDelete from "components/buttons/actionDelete";

/* ===================================================== */

const TimeZoneMaster = () => {
  const [list, setList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    fetchList();
  }, []);

  const toggle = () => setIsOpen(!isOpen);

  /* ================= FETCH ================= */

  const fetchList = () => {
    apiGet("SysTimeZone/all")
      .then(res => setList(res.data))
      .catch(() => toast.error("Failed to load time zones"));
  };

  /* ================= CREATE ================= */

  const handleCreate = (values, { setSubmitting, resetForm }) => {
    toast.loading("Saving...");

    apiPost("SysTimeZone", values)
      .then(() => {
        toast.dismiss();
        toast.success("Saved successfully");
        fetchList();
        resetForm();
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Save failed");
      })
      .finally(() => setSubmitting(false));
  };

  /* ================= UPDATE ================= */

  const handleUpdate = (values, { setSubmitting }) => {
    toast.loading("Updating...");

    apiPut("SysTimeZone", values)
      .then(() => {
        toast.dismiss();
        toast.success("Updated successfully");
        fetchList();
        toggle();
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Update failed");
      })
      .finally(() => setSubmitting(false));
  };

  /* ================= DELETE ================= */

  const handleDelete = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will be deleted permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
    }).then(result => {
      if (!result.isConfirmed) return;

      apiDelete(`SysTimeZone/${id}`)
        .then(() => {
          toast.success("Deleted successfully");
          fetchList();
        })
        .catch(() => toast.error("Delete failed"));
    });
  };

  const openEdit = item => {
    setSelected(item);
    toggle();
  };

  /* ================= VALIDATION ================= */

  const validationSchema = Yup.object({
    tzId: Yup.string().required("TZ Id required"),
    countryCode: Yup.string().required("Country code required"),
    gmtstdOffsetMinutes: Yup.number().required("Required"),
    standardAbbrivation: Yup.string().required("Required"),
  });

  /* ================= RENDER ================= */

  return (
    <Container fluid className="page-content">
      <ToastContainer autoClose={2000} />
        <Row className="mb-20">
            <Col>
                <Breadcrumb title="Settings" breadcrumbItem="System Time Zone" />
            </Col>
        </Row>
      {/* ================= ADD ================= */}
      <Row>
        <Col xl="12">
          <Card>
            <CardBody>
             

              <Formik
                initialValues={{
                  tzId: "",
                  countryCode: "",
                  gmtstdOffsetMinutes: 0,
                  gmtstdOffsetName: "",
                  standardAbbrivation: "",
                  gmtdstOffsetMinutes: 0,
                  gmtdstOffsetName: "",
                  dstAbbrivation: "",
                  isDSTObserved: false,
                  isActive: true,
                  isTwilioIntegrationEnabled: false,
                }}
                validationSchema={validationSchema}
                onSubmit={handleCreate}
              >
                {({ handleChange }) => (
                  <Form>
                    <Row>
                      <Col md="3">
                        <FormGroup>
                          <Label>TZ Id *</Label>
                          <SafeField name="tzId" className="form-control" />
                        </FormGroup>
                      </Col>

                      <Col md="2">
                        <FormGroup>
                          <Label>Country *</Label>
                          <SafeField name="countryCode" className="form-control" />
                        </FormGroup>
                      </Col>

                      <Col md="2">
                        <FormGroup>
                          <Label>GMT Std Offset (min)</Label>
                          <SafeField
                            name="gmtstdOffsetMinutes"
                            type="number"
                            className="form-control"
                          />
                        </FormGroup>
                      </Col>

                      <Col md="2">
                        <FormGroup>
                          <Label>Std Abbr *</Label>
                          <SafeField
                            name="standardAbbrivation"
                            className="form-control"
                          />
                        </FormGroup>
                      </Col>

                      <Col md="3" className="">
                        <Button type="submit" color="primary" className="mt-4">
                          Save
                        </Button>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="3">
                        <FormGroup check>
                          <Field type="checkbox" name="isDSTObserved" />{" "}
                          <Label check>DST Observed</Label>
                        </FormGroup>
                      </Col>

                      <Col md="3">
                        <FormGroup check>
                          <Field type="checkbox" name="isTwilioIntegrationEnabled" />{" "}
                          <Label check>Twilio Enabled</Label>
                        </FormGroup>
                      </Col>

                      <Col md="3">
                        <FormGroup check>
                          <Field type="checkbox" name="isActive" />{" "}
                          <Label check>Active</Label>
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </CardBody>
          </Card>

          {/* ================= LIST ================= */}
          <Card>
            <CardBody>
              <h5>Time Zone List</h5>

              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>TZ</th>
                      <th>Country</th>
                      <th>Std Offset</th>
                      <th>DST</th>
                      <th>Twilio</th>
                      <th>Active</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {list.map((item, i) => (
                      <tr key={item.timeZoneId || i}>
                        <td>{item.tzId}</td>
                        <td>{item.countryCode}</td>
                        <td>{item.gmtstdOffsetMinutes}</td>
                        <td>{item.isDSTObserved ? "Yes" : "No"}</td>
                        <td>{item.isTwilioIntegrationEnabled ? "Yes" : "No"}</td>
                        <td>{item.isActive ? "Yes" : "No"}</td>
                        <td>
                            <ActionEdit onClick={() => openEdit(item)} />{" "}
                            <ActionDelete onClick={() => handleDelete(item.id)} />
                         
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ================= EDIT ================= */}
      <Offcanvas isOpen={isOpen} toggle={toggle} direction="end">
        <OffcanvasHeader toggle={toggle}>Update Time Zone</OffcanvasHeader>
        <OffcanvasBody>
          <Formik
            enableReinitialize
            initialValues={selected}
            validationSchema={validationSchema}
            onSubmit={handleUpdate}
          >
            {() => (
              <Form>
                <FormGroup>
                  <Label>TZ Id</Label>
                  <SafeField name="tzId" className="form-control" />
                </FormGroup>

                <FormGroup>
                  <Label>Country</Label>
                  <SafeField name="countryCode" className="form-control" />
                </FormGroup>

                <FormGroup>
                  <Label>Std Offset Minutes</Label>
                  <SafeField
                    name="gmtstdOffsetMinutes"
                    type="number"
                    className="form-control"
                  />
                </FormGroup>

                <FormGroup check className="mb-2">
                  <SafeField type="checkbox" name="isDSTObserved" />{" "}
                  <Label check>DST Observed</Label>
                </FormGroup>

                <FormGroup check className="mb-2">
                  <SafeField
                    type="checkbox"
                    name="isTwilioIntegrationEnabled"
                  />{" "}
                  <Label check>Twilio Enabled</Label>
                </FormGroup>

                <FormGroup check className="mb-3">
                  <Field type="checkbox" name="isActive" />{" "}
                  <Label check>Active</Label>
                </FormGroup>

                <Button type="submit" color="primary">
                  Update
                </Button>
              </Form>
            )}
          </Formik>
        </OffcanvasBody>
      </Offcanvas>
    </Container>
  );
};

export default TimeZoneMaster;
